import type { SplinePose } from '../core/splineDrive/useSplineDrive';

/** Side-arm raise into the hopper (pickup). Positive Z would dig into the ground. */
export function sideArmRotationZ(armRaise: number, armTip: number): number {
  return -0.2 - armRaise * 1.2 - armTip * 0.4;
}

/** Claw tip while dumping into the hopper during pickup. */
export function clawRotationZ(armTip: number): number {
  return armTip * 0.9;
}

/**
 * Hopper tip angle at the plant after reverse-in (like a dump truck / tipping refuse body).
 * Positive Z lifts the front (+X) of the hopper while the rear hinge stays on the floor.
 */
export function tipDumpBodyRotationZ(): number {
  return 0.62;
}

/** Rear hinge (local X/Y) of the hopper when tipping at the plant. */
export const TIP_PIVOT = { x: -2.0, y: 0.4 } as const;

/**
 * Transform for the hopper group at tip `progress` (0 = level, 1 = fully tipped): rotate
 * about the rear hinge (not the group origin) so the rear stays on the floor and the
 * front lifts. The cab, chassis and wheels do not move.
 */
export function tipDumpBodyTransform(progress: number): { rotZ: number; x: number; y: number } {
  const p = Math.max(0, Math.min(1, progress));
  if (p <= 0) return { rotZ: 0, x: 0, y: 0 };
  const theta = tipDumpBodyRotationZ() * p;
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  // position = P - R·P keeps the pivot point fixed under the rotation
  return {
    rotZ: theta,
    x: TIP_PIVOT.x - (TIP_PIVOT.x * c - TIP_PIVOT.y * s),
    y: TIP_PIVOT.y - (TIP_PIVOT.x * s + TIP_PIVOT.y * c),
  };
}

export interface FinishLandmark {
  x: number;
  z: number;
  yaw: number;
}

/**
 * Pose during the finish approach: turn 180° in place, then reverse to the landmark.
 * `u` is 0..1 over the full maneuver.
 */
export function finishApproachPose(
  from: Pick<SplinePose, 'x' | 'z' | 'yaw'>,
  landmark: FinishLandmark,
  u: number
): SplinePose {
  const t = Math.max(0, Math.min(1, u));
  const turnedYaw = landmark.yaw + Math.PI;
  if (t < 0.35) {
    const local = t / 0.35;
    const eased = local * local * (3 - 2 * local);
    return {
      x: from.x,
      z: from.z,
      yaw: from.yaw + Math.PI * eased,
      t: 1,
    };
  }
  const local = (t - 0.35) / 0.65;
  const eased = local * local * (3 - 2 * local);
  return {
    x: from.x + (landmark.x - from.x) * eased,
    z: from.z + (landmark.z - from.z) * eased,
    yaw: turnedYaw,
    t: 1,
  };
}

/**
 * Desired follow-cam position for a phase.
 * Finish must stand outside the plant hall (in front of the reversed truck).
 * The old "behind vehicle" follow put the camera past the roof so pointer rays
 * hit the plant roof and the dump tap never reached the truck.
 */
export function followCamDesired(
  target: Pick<SplinePose, 'x' | 'z' | 'yaw'>,
  worldPhase: 'start' | 'play' | 'finish' | string
): { x: number; y: number; z: number } {
  if (worldPhase === 'finish') {
    const back = 6;
    // In front of the truck (entrance side) after the 180° reverse-in.
    return {
      x: target.x + Math.cos(target.yaw) * back,
      y: 7.2,
      z: target.z + Math.sin(target.yaw) * back + 7.5,
    };
  }
  const back = worldPhase === 'start' ? 8 : 10;
  const height = worldPhase === 'start' ? 6.5 : 7.5;
  return {
    x: target.x - Math.cos(target.yaw) * back,
    y: height,
    z: target.z - Math.sin(target.yaw) * back + 8,
  };
}

/** No-op raycast so decorative meshes never steal truck/bin taps. */
export function noRaycast(_raycaster?: unknown, _intersects?: unknown): void {
  /* intentionally empty — assign to mesh.raycast */
}
